import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import CallToAction from '../components/CallToAction';

interface FaqItem {
  question: string;
  answer: string;
}

const faqData: FaqItem[] = [
  {
    question: "Siete il servizio clienti ufficiale del mio fornitore di energia?",
    answer: "No. Servizio Bolletta è un servizio indipendente di comparazione e consulenza commerciale gestito da GT Energie Srl. Non siamo il servizio clienti ufficiale di alcun fornitore di energia, gas o servizi internet. Non rappresentiamo in modo esclusivo alcun operatore del settore."
  },
  {
    question: "Come funziona il servizio di comparazione?",
    answer: "Il nostro servizio confronta le offerte di diversi fornitori di energia, gas e internet disponibili sul mercato. Un nostro consulente analizza le tue esigenze e ti presenta le opzioni più adatte. Il servizio è completamente gratuito e non comporta alcun obbligo di sottoscrizione."
  },
  {
    question: "Il servizio è gratuito?",
    answer: "Sì, il servizio di consulenza e comparazione offerte è completamente gratuito per l'utente. Non applichiamo alcun costo, commissione o spesa a carico del consumatore. In alcuni casi, Servizio Bolletta potrebbe percepire una commissione commerciale dai fornitori partner qualora il cliente decida autonomamente di attivare un'offerta. Questo non influisce sul costo dell'offerta per il cliente."
  },
  {
    question: "Posso ricevere informazioni senza attivare un contratto?",
    answer: "Assolutamente sì. Puoi richiedere una consulenza, confrontare le offerte e ricevere tutte le informazioni di cui hai bisogno senza alcun obbligo di attivazione. La decisione di sottoscrivere un contratto con un fornitore è sempre libera, autonoma e a tua totale discrezione."
  },
  {
    question: "Chi gestisce il servizio Servizio Bolletta?",
    answer: "Servizio Bolletta è un marchio commerciale gestito da GT Energie Srl, con sede legale in Via Filippo Argelati 10, 20143 Milano (MI), Italia. P.IVA: IT02812720809. Operiamo come intermediario indipendente nel settore energetico e delle telecomunicazioni."
  },
  {
    question: "Le offerte mostrate sul sito sono vincolanti?",
    answer: "No. Le informazioni, i prezzi e le offerte presentati sul nostro sito hanno carattere puramente indicativo e informativo. I prezzi e le condizioni possono variare in qualsiasi momento su decisione dei fornitori. Per le condizioni contrattuali definitive e vincolanti, è sempre necessario fare riferimento alla documentazione ufficiale del fornitore prescelto."
  },
  {
    question: "Come guadagna Servizio Bolletta se il servizio è gratuito?",
    answer: "In alcuni casi, Servizio Bolletta percepisce una commissione commerciale dai fornitori partner quando un cliente decide autonomamente di attivare un'offerta tramite il nostro servizio. Questo modello ci consente di offrire la consulenza gratuitamente e non comporta alcun costo aggiuntivo per il cliente rispetto all'offerta del fornitore."
  },
];

const Faq: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Domande Frequenti</h1>
            <p className="text-xl text-white/80">
              Risposte alle domande più comuni sul nostro servizio di comparazione e consulenza
            </p>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-primary/5 border-b border-primary/10">
        <div className="container mx-auto py-4 px-4">
          <div className="flex items-center gap-3 justify-center text-center">
            <FontAwesomeIcon icon={faInfoCircle} className="text-primary flex-shrink-0" />
            <p className="text-sm text-light-muted">
              Servizio Bolletta è un servizio indipendente di comparazione gestito da GT Energie Srl — 
              Non siamo il servizio clienti ufficiale di alcun fornitore di energia.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-white rounded-lg border border-light-border/40 shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => toggle(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-light-background/50 transition-colors"
                >
                  <span className="font-semibold text-light-text pr-4">{faq.question}</span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="px-6 pb-6 border-t border-light-border/30 pt-4">
                        <p className="text-light-muted text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="py-12 px-4"
      >
        <div className="container mx-auto">
          <CallToAction
            title="Hai altre domande?"
            subtitle="Contatta un nostro consulente per una consulenza gratuita e senza impegno"
            variant="primary"
            className="mt-8"
          />
        </div>
      </motion.section>
    </div>
  );
};

export default Faq;
